import React from 'react';
import type { ChecklistStep } from '../../utils/onboarding';
import { checklistProgress } from '../../utils/onboarding';

interface OnboardingWelcomeCardProps {
  onAddVehicle: () => void;
  onExplore: () => void;
  onSkip: () => void;
}

export const OnboardingWelcomeCard: React.FC<OnboardingWelcomeCardProps> = ({
  onAddVehicle,
  onExplore,
  onSkip,
}) => (
  <section
    className="rounded-2xl border border-white/[0.08] bg-[#0c0c0c]/80 p-4 sm:p-6 mb-6"
    aria-label="Welcome to Bilt:Spec"
  >
    <p className="kicker mb-2">Welcome to Bilt:Spec</p>
    <h2 className="heading-section mb-2">Tell the story behind your build</h2>
    <p className="text-muted text-sm leading-relaxed max-w-xl mb-5">
      Keep your specifications, parts, photos, events, awards, and milestones together as the car
      evolves. Start with the vehicle. You can finish the rest later.
    </p>
    <div className="flex flex-col sm:flex-row gap-2">
      <button type="button" onClick={onAddVehicle} className="btn-primary min-h-[44px]">
        Start with a vehicle
      </button>
      <button type="button" onClick={onExplore} className="btn-secondary min-h-[44px]">
        Explore first
      </button>
      <button type="button" onClick={onSkip} className="btn-ghost min-h-[44px] text-sm">
        Skip for now
      </button>
    </div>
  </section>
);

interface SetupChecklistCardProps {
  title?: string;
  description?: string;
  steps: ChecklistStep[];
  onDismiss: () => void;
  onStepAction?: (step: ChecklistStep) => void;
}

export const SetupChecklistCard: React.FC<SetupChecklistCardProps> = ({
  title = 'Your build is taking shape',
  description,
  steps,
  onDismiss,
  onStepAction,
}) => {
  const { done, total } = checklistProgress(steps);
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <section className="rounded-2xl border border-white/[0.08] bg-[#0c0c0c]/80 p-4 mb-6" aria-label={title}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h2 className="heading-card">{title}</h2>
          <p className="text-muted text-xs mt-0.5" aria-live="polite">
            {description || `${done} of ${total} complete`}
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="btn-ghost btn-sm text-xs shrink-0 min-h-[44px]"
          aria-label="Hide checklist"
        >
          Hide checklist
        </button>
      </div>
      <div
        className="h-1 rounded-full bg-[#141414] overflow-hidden mb-3"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Setup progress"
      >
        <div
          className="h-full bg-orange-500/80 transition-[width] duration-300 motion-reduce:transition-none"
          style={{ width: `${percent}%` }}
        />
      </div>
      <ul className="space-y-1">
        {steps.map((step) => (
          <li key={step.id}>
            <button
              type="button"
              disabled={step.done && !step.href && !step.actionId}
              onClick={() => onStepAction?.(step)}
              className={`w-full flex items-center gap-2.5 text-left rounded-lg px-2 py-2.5 min-h-[44px] ${
                step.done ? 'text-muted' : 'text-gray-200 hover:bg-white/[0.05]'
              }`}
            >
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] shrink-0 border ${
                  step.done
                    ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/35'
                    : 'border-white/[0.12] text-gray-500'
                }`}
                aria-hidden
              >
                {step.done ? '✓' : ''}
              </span>
              <span className={`text-sm ${step.done ? 'line-through decoration-white/20' : ''}`}>
                {step.label}
              </span>
              <span className="sr-only">{step.done ? 'Completed' : 'Not completed'}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};

interface OnboardingResumeCardProps {
  onContinue: () => void;
}

export const OnboardingResumeCard: React.FC<OnboardingResumeCardProps> = ({ onContinue }) => (
  <section
    className="rounded-2xl border border-white/[0.08] bg-[#0c0c0c]/70 px-4 py-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
    aria-label="Continue setup"
  >
    <div>
      <h2 className="heading-card text-base">Continue your build setup</h2>
      <p className="text-muted text-xs mt-0.5">A few useful steps are still open in your garage.</p>
    </div>
    <button type="button" onClick={onContinue} className="btn-secondary min-h-[44px] shrink-0">
      Continue setup
    </button>
  </section>
);

interface ContextualTipProps {
  tipId: string;
  children: React.ReactNode;
  onDismiss: (tipId: string) => void;
  className?: string;
}

export const ContextualTip: React.FC<ContextualTipProps> = ({
  tipId,
  children,
  onDismiss,
  className = '',
}) => (
  <div
    className={`rounded-xl border border-white/[0.08] bg-[#0c0c0c]/90 px-3 py-2.5 text-xs text-muted flex gap-2 items-start ${className}`}
    role="note"
  >
    <div className="flex-1 leading-relaxed">{children}</div>
    <button
      type="button"
      onClick={() => onDismiss(tipId)}
      className="btn-ghost btn-sm shrink-0 min-w-[44px] min-h-[44px] px-1"
      aria-label="Dismiss tip"
    >
      <span aria-hidden>×</span>
    </button>
  </div>
);

interface JudgeGuideCardProps {
  onDismiss: () => void;
}

export const JudgeGuideCard: React.FC<JudgeGuideCardProps> = ({ onDismiss }) => (
  <section className="rounded-2xl border border-white/[0.08] bg-[#0c0c0c]/80 p-4 mb-6" aria-label="How judging works">
    <div className="flex items-start justify-between gap-3 mb-2">
      <h2 className="heading-card">How judging works</h2>
      <button type="button" onClick={onDismiss} className="btn-ghost btn-sm text-xs min-h-[44px]">
        Got it
      </button>
    </div>
    <ol className="text-sm text-muted space-y-2 list-decimal list-inside">
      <li>Open your assigned event and check progress</li>
      <li>Filter by class, then start or continue judging</li>
      <li>Use Score, Build, and Photos tabs. Drafts stay on the scorecard</li>
      <li>Finalize only when all criteria are set</li>
    </ol>
  </section>
);

interface RoleOnboardingCardProps {
  title: string;
  body: string;
  onDismiss: () => void;
  dismissLabel?: string;
}

export const RoleOnboardingCard: React.FC<RoleOnboardingCardProps> = ({
  title,
  body,
  onDismiss,
  dismissLabel = 'Got it',
}) => (
  <section
    className="rounded-2xl border border-white/[0.08] bg-[#0c0c0c]/80 p-4 mb-6"
    aria-label={title}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 className="heading-card mb-1">{title}</h2>
        <p className="text-muted text-sm leading-relaxed">{body}</p>
      </div>
      <button type="button" onClick={onDismiss} className="btn-ghost btn-sm text-xs shrink-0 min-h-[44px]">
        {dismissLabel}
      </button>
    </div>
  </section>
);
